import { useQuery } from "@apollo/client";
import { LOAD_VEHICLE_CATEGORIES_QUERY } from "globals/graphql";
import map from "lodash/map";
import filter from "lodash/filter";
import some from "lodash/some";
import { useMemo } from "react";

import { CreateTripState } from "pages/new/context/initialState";
import { formatStopsForBRACalculations } from "pages/new/utils/formatStopsForBRACalculations";
import { Contact } from "types";

// Temporary type definition until server types are available
type LoadVehicleCategoriesConnection = {
  totalCount: number;
  edges: Array<{
    node: {
      id: string;
      name: string;
      capacity: number;
      enableBaseRateAutomation: boolean;
      baseRateAutomation?: {
        total: number;
        lineItems: Array<{
          name: string;
          description?: string;
          lineTotal: number;
          source: string;
        }>;
      };
      returnBaseRateAutomation?: {
        total: number;
        lineItems: Array<{
          name: string;
          description?: string;
          lineTotal: number;
          source: string;
        }>;
      };
      hasTripConflict?: boolean | null;
      hasUnavailabilityBlock?: boolean | null;
      photos?: Array<{
        url: string;
        index: number;
      }>;
      customerPortalSettings?: {
        available: boolean;
        enableBaseRateAutomationBookingTool: boolean;
        conflictBlockQuote: boolean;
        conflictBlockReservation: boolean;
      };
      features?: Array<{
        id: string;
        name: string;
        category: string;
      }>;
      operator?: {
        id: string;
        name: string;
      };
      contacts?: Array<{
        id: string;
      }>;
      companies?: Array<{
        id: string;
      }>;
    };
  }>;
};

type UseLoadVehicleCategoriesProps = {
  trip: CreateTripState;
  returnTrip: CreateTripState;
  searchTerm?: string;
  enabled?: boolean;
  authenticatedUser?: Contact | null;
};

function useLoadVehicleCategories(props: UseLoadVehicleCategoriesProps) {
  const {
    trip,
    returnTrip,
    searchTerm,
    enabled = true,
    authenticatedUser,
  } = props;

  const { vehicleStops, returnTripStops } = formatStopsForBRACalculations({
    trip,
    returnTrip,
  });

  // Determine if we should calculate BRA based on whether we have stops
  const shouldCalculateBRA = Boolean(vehicleStops?.length);

  // Single query that conditionally includes BRA calculation
  const { data, loading } = useQuery<{
    loadVehicleCategories: LoadVehicleCategoriesConnection;
  }>(LOAD_VEHICLE_CATEGORIES_QUERY, {
    skip: !enabled,
    variables: {
      searchTerm,
      stops: vehicleStops,
      returnTripStops: returnTripStops,
      ...(shouldCalculateBRA && {
        shouldCalculateBRA: true,
      }),
    },
    fetchPolicy: "network-only",
  });

  // Use memoized vehicle categories with filtering based on authenticated user
  const vehicleCategories = useMemo(() => {
    const allCategories = map(data?.loadVehicleCategories.edges, "node") || [];

    return filter(allCategories, (category) => {
      // If category has no specific contacts/companies, it's available to all
      const hasNoRestrictions =
        (!category.contacts || category.contacts.length === 0) &&
        (!category.companies || category.companies.length === 0);

      if (hasNoRestrictions) return true;

      // Category has restrictions, so user must be authenticated
      if (!authenticatedUser) return false;

      // Check if user matches the restrictions
      const isContactMatch = some(
        category.contacts,
        (contact) => contact.id === authenticatedUser.id
      );

      const isCompanyMatch =
        authenticatedUser.company &&
        some(
          category.companies,
          (company) => company.id === authenticatedUser.company?.id
        );

      return isContactMatch || isCompanyMatch;
    });
  }, [data, authenticatedUser]);

  const totalCount = useMemo(() => {
    return data?.loadVehicleCategories.totalCount || 0;
  }, [data]);

  return {
    vehicleCategories,
    totalCount,
    isLoadingVehicleCategories: loading,
  };
}

export default useLoadVehicleCategories;
